import type {
  ActiveSessionStatus,
  ChatMessageRequest,
  ChatMessageResponse,
  GetSessionResponse,
  StartSessionRequest,
  StartSessionResponse,
} from '~/types';

import { apiRequest } from './client';
import { HTTP_METHODS } from './config';

/**
 * Response from cancel breakdown endpoint.
 */
export interface CancelBreakdownResponse {
  status: 'cancelled';
  job_id: string;
  project_id: string;
}

/**
 * Single job entry in project breakdown history.
 */
export interface JobHistoryItem {
  job_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  current_phase: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  tasks_created_count: number | null;
  has_tech_spec: boolean;
}

/**
 * Request to initiate PRD breakdown (task generation from PRD content).
 * This is separate from PRD generation (Story 4.1) - this is for breaking down existing PRDs into tasks.
 *
 * Note: prd_content is optional - backend will fetch from PRDDocument if not provided.
 */
export interface PRDBreakdownRequest {
  project_id: string;
  prd_content?: string;
  prd_id?: string;
  stream_events?: boolean;
  // Note: user_id is NOT included - backend extracts from Firebase token automatically
}

/**
 * Response from PRD breakdown initiation.
 */
export interface PRDBreakdownResponse {
  job_id: string;
  project_id: string;
  status: string;
  sse_url: string;
  estimated_duration: number;
  message: string;
}

/**
 * Comprehensive project breakdown status for real-time UI updates.
 * Includes job state, document existence, and task counts.
 */
export interface ProjectBreakdownStatus {
  breakdown_status: 'idle' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  has_active_breakdown: boolean;
  job_id: string | null;
  current_phase: string | null;
  breakdown_created_at: string | null;
  breakdown_started_at: string | null;
  breakdown_completed_at: string | null;
  has_prd: boolean;
  has_technical_spec: boolean;
  task_count: number;
}

/**
 * Batch response for all user's project breakdown statuses.
 */
export interface ProjectBreakdownStatusBatch {
  projects: ProjectBreakdownStatusBatchItem[];
}

/**
 * Single project status in batch response.
 */
export interface ProjectBreakdownStatusBatchItem {
  project_id: string;
  breakdown_status: 'idle' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  has_active_breakdown: boolean;
  job_id: string | null;
  current_phase: string | null;
  breakdown_created_at: string | null;
  breakdown_started_at: string | null;
  breakdown_completed_at: string | null;
  has_prd: boolean;
  has_technical_spec: boolean;
  task_count: number;
}

/**
 * Job history response for a project.
 */
export interface ProjectJobHistoryResponse {
  project_id: string;
  jobs: JobHistoryItem[];
}

/**
 * Batch response for all user's project session statuses.
 */
export interface ProjectSessionStatusBatch {
  projects: ProjectSessionStatusBatchItem[];
}

/**
 * PRD session status for a single project in batch response.
 *
 * Note: 'idle' is frontend-only status. Backend returns null for projects without sessions.
 * Frontend code should treat status === null as 'idle'.
 */
export interface ProjectSessionStatusBatchItem {
  project_id: string;
  session_id: string | null;
  status: 'idle' | 'active' | 'paused' | 'completed' | 'expired' | 'failed' | null;
  session_state: 'awaiting_answers' | 'generating' | 'ready_for_finalization' | null;
  generation_phase: string | null;
  completeness_score: number;
  question_count: number;
  answered_question_count: number;
  created_at: string | null;
  updated_at: string | null;
  is_owner: boolean;
}

/**
 * Initiate PRD breakdown workflow (task generation from PRD content).
 *
 * This is separate from PRD generation (Story 4.1). This function is for breaking down
 * existing PRD content into tasks using the PRDBreakdownAgent.
 *
 * @param request - PRD breakdown request with project_id and prd_content
 * @returns Promise resolving to breakdown response with job_id
 * @throws {ApiError} 404 - Project not found
 * @throws {ApiError} 422 - Validation error
 * @throws {ApiError} 503 - Agent service unavailable
 * @throws {ApiError} 500 - Server error
 */
export async function breakdownPRD(request: PRDBreakdownRequest): Promise<PRDBreakdownResponse> {
  const response = await apiRequest<PRDBreakdownResponse>('/prd/breakdown', {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(request),
  });

  return response.data;
}

/**
 * Cancel the active PRD breakdown for a project (soft cancel).
 *
 * Note: This is a "soft" cancel - it updates the job status to CANCELLED
 * but the agent processing continues until completion. Tasks may still be created.
 * Full cancellation support will be added in a future update.
 *
 * @param projectId - Project UUID to cancel breakdown for
 * @returns Promise resolving to cancel response with job_id
 * @throws {ApiError} 404 - No active breakdown for this project
 * @throws {ApiError} 400 - Job already completed
 * @throws {ApiError} 500 - Server error
 */
export async function cancelBreakdown(projectId: string): Promise<CancelBreakdownResponse> {
  const response = await apiRequest<CancelBreakdownResponse>(`/prd/breakdown/cancel/${projectId}`, {
    method: HTTP_METHODS.POST,
  });

  return response.data;
}

/**
 * Cancel a PRD generation session.
 *
 * Soft-deletes the session, releasing the project lock so other users
 * can start new sessions. Only the session owner can cancel.
 *
 * @param sessionId - Session UUID to cancel
 * @throws {ApiError} 404 - Session not found or user doesn't own it
 * @throws {ApiError} 429 - Rate limit exceeded
 * @throws {ApiError} 500 - Server error
 */
export async function cancelSession(sessionId: string): Promise<void> {
  await apiRequest<void>(`/prd/generate/${sessionId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Check if PRD generation is active for a project.
 *
 * Lightweight status check for polling. Returns whether generation is active
 * and if the current user owns the session.
 *
 * @param projectId - Project UUID to check
 * @returns Promise resolving to active session status
 * @throws {ApiError} 500 - Server error
 */
export async function checkActiveSession(projectId: string): Promise<ActiveSessionStatus> {
  const response = await apiRequest<ActiveSessionStatus>(`/prd/generate/active/${projectId}`);

  return response.data;
}

/**
 * Continue PRD generation session with answers.
 *
 * @param request - Session continue request with answers
 * @returns Promise resolving to session continue response
 * @throws {ApiError} 400 - Invalid request
 * @throws {ApiError} 404 - Session not found/expired
 * @throws {ApiError} 403 - Unauthorized (wrong user)
 * @throws {ApiError} 429 - Rate limit exceeded
 * @throws {ApiError} 500 - Server error
 */
export async function getBreakdownHistory(
  projectId: string,
  limit: number = 10,
): Promise<ProjectJobHistoryResponse> {
  const response = await apiRequest<ProjectJobHistoryResponse>(
    `/prd/breakdown/history/${projectId}?limit=${limit}`,
    {
      method: HTTP_METHODS.GET,
    },
  );

  return response.data;
}

/**
 * Get comprehensive breakdown status for a project.
 *
 * Returns the complete state including active job status, documents, and task counts.
 * Used for status polling and cross-channel visibility (MCP, CLI, web UI).
 *
 * @param projectId - Project UUID to get status for
 * @returns Promise resolving to project breakdown status
 * @throws {ApiError} 404 - Project not found or no access
 * @throws {ApiError} 500 - Server error
 */
export async function getBreakdownStatus(projectId: string): Promise<ProjectBreakdownStatus> {
  const response = await apiRequest<ProjectBreakdownStatus>(`/prd/breakdown/status/${projectId}`, {
    method: HTTP_METHODS.GET,
  });

  return response.data;
}

/**
 * Get breakdown status for all user's projects in a single batch query.
 *
 * Optimizes polling by fetching all project statuses at once instead of N individual queries.
 * Reduces API load from N queries to 1 batch query when displaying project lists.
 *
 * @returns Promise resolving to batch status for all accessible projects
 * @throws {ApiError} 500 - Server error
 */
export async function getBreakdownStatusBatch(): Promise<ProjectBreakdownStatusBatch> {
  const response = await apiRequest<ProjectBreakdownStatusBatch>('/prd/breakdown/status/batch', {
    method: HTTP_METHODS.GET,
  });

  return response.data;
}

/**
 * Get PRD preview for session.
 *
 * @param sessionId - Session ID to get preview for
 * @returns Promise resolving to preview response
 * @throws {ApiError} 404 - Session not found/expired
 * @throws {ApiError} 403 - Unauthorized (wrong user)
 */
export async function getSession(sessionId: string): Promise<GetSessionResponse> {
  const response = await apiRequest<GetSessionResponse>(`/prd/generate/${sessionId}`);

  return response.data;
}

/**
 * Get PRD session status for all user's projects in a single batch query.
 *
 * Efficiently retrieves session status for all projects the user has access to,
 * enabling batch polling with a single API call instead of N individual requests.
 *
 * @returns Promise resolving to batch response with all project session statuses
 * @throws {ApiError} 401 - Unauthorized
 * @throws {ApiError} 429 - Rate limit exceeded (500/hour)
 * @throws {ApiError} 500 - Server error
 */
export async function getSessionStatusBatch(): Promise<ProjectSessionStatusBatch> {
  const response = await apiRequest<ProjectSessionStatusBatch>('/prd/generate/status/batch', {
    method: HTTP_METHODS.GET,
  });

  return response.data;
}

/**
 * Send a chat message for in-question discussion.
 *
 * Used when user wants to discuss/clarify before formally answering.
 * Agent may detect an answer in the message and suggest confirmation.
 *
 * @param request - Chat message request
 * @returns Promise resolving to agent response with optional answer detection
 * @throws {ApiError} 400 - Invalid request (empty message)
 * @throws {ApiError} 404 - Session not found/expired
 * @throws {ApiError} 403 - Unauthorized (wrong user)
 * @throws {ApiError} 429 - Rate limit exceeded (200/hour)
 * @throws {ApiError} 500 - Server error
 */
export async function sendChatMessage(request: ChatMessageRequest): Promise<ChatMessageResponse> {
  // Chat responses should be faster than full answer processing
  const response = await apiRequest<ChatMessageResponse>('/prd/generate/chat', {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(request),
    timeout: 30000, // 30 seconds for chat response
  });

  return response.data;
}

// ============================================================================
// Chat Interface Endpoints (Single-Question Conversational Flow)
// ============================================================================

/**
 * Start PRD generation session.
 *
 * @param request - Session start request with project information
 * @returns Promise resolving to session start response
 * @throws {ApiError} 401 - Unauthorized (missing/invalid JWT)
 * @throws {ApiError} 404 - Project not found
 * @throws {ApiError} 409 - Conflict (another user is generating PRD for this project)
 * @throws {ApiError} 429 - Rate limit exceeded
 * @throws {ApiError} 500 - Server error
 */
export async function startSession(request: StartSessionRequest): Promise<StartSessionResponse> {
  // PRD generation can exceed 2 minutes under heavier model/tenant load.
  const response = await apiRequest<StartSessionResponse>('/prd/generate/start', {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(request),
    timeout: 300000, // 5 minutes to avoid false client timeouts while backend continues streaming
  });

  return response.data;
}

/**
 * Submit a single answer and advance to next question.
 *
 * Used in the chat interface when user formally submits an answer
 * (either directly or after confirmation from chat message detection).
 *
 * @param request - Answer submission request
 * @returns Promise resolving to response with next question and updated PRD
 * @throws {ApiError} 400 - Invalid request (empty answer, invalid question_id)
 * @throws {ApiError} 404 - Session not found/expired
 * @throws {ApiError} 403 - Unauthorized (wrong user)
 * @throws {ApiError} 429 - Rate limit exceeded (100/hour)
 * @throws {ApiError} 500 - Server error
 */
